import { createJsonMemoryAccessors } from '@/services/storage'

import { SnapshotCompare } from './SnapshotCompare'
import { SnapshotValidator } from './SnapshotValidator'
import { SnapshotVersion } from './SnapshotVersion'
import type {
  CreateSnapshotInput,
  QuoteSnapshot,
  SnapshotExportPayload,
  SnapshotHistoryEntry,
  SnapshotHistoryEvent,
  SnapshotItem,
  SnapshotSearchFilters,
  SnapshotStatus,
  SnapshotValidityDays,
} from './types'

const SNAPSHOTS_KEY = 'masterhub.snapshots.v1'
const HISTORY_KEY = 'masterhub.snapshot-history.v1'

type Memory = {
  snapshots: QuoteSnapshot[] | null
  history: SnapshotHistoryEntry[] | null
}

const memory: Memory = {
  snapshots: null,
  history: null,
}

const { readJson, writeJson } = createJsonMemoryAccessors(memory)

function nowIso(): string {
  return new Date().toISOString()
}

function addDays(iso: string, days: number): string {
  const date = new Date(iso)
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

/**
 * SnapshotService — persistence + CRUD for immutable quote photographs.
 */
export class SnapshotService {
  private readonly validator = new SnapshotValidator()
  private readonly compareEngine = new SnapshotCompare()

  listAll(): QuoteSnapshot[] {
    return this.refreshStatuses(readJson<QuoteSnapshot[]>(SNAPSHOTS_KEY, 'snapshots', []))
  }

  listByQuote(quoteId: string): QuoteSnapshot[] {
    return SnapshotVersion.sortDesc(this.listAll().filter((s) => s.quoteId === quoteId))
  }

  getById(id: string): QuoteSnapshot | null {
    const found = this.listAll().find((s) => s.id === id) ?? null
    if (!found) return null
    return this.refreshOne(found)
  }

  getLatest(quoteId: string): QuoteSnapshot | null {
    return this.listByQuote(quoteId)[0] ?? null
  }

  create(input: CreateSnapshotInput): QuoteSnapshot {
    const errors = this.validator.validateCreateInput(input)
    if (errors.length > 0) throw new Error(errors.join('; '))

    const existing = this.listByQuote(input.quoteId)
    const version = SnapshotVersion.next(existing.map((s) => s.version))
    const createdAt = nowIso()
    const id = crypto.randomUUID()
    const createdBy = input.createdBy ?? 'admin@masterhub.com'

    const items: SnapshotItem[] = input.items.map((item) => ({
      id: crypto.randomUUID(),
      snapshotId: id,
      productId: item.productId,
      description: item.description,
      supplier: item.supplier ?? null,
      supplierCode: item.supplierCode ?? null,
      supplierLink: item.supplierLink ?? null,
      purchasePrice: item.purchasePrice,
      pixPrice: item.pixPrice ?? item.purchasePrice,
      cardPrice: item.cardPrice ?? item.salePrice,
      installmentPrice: item.installmentPrice ?? item.salePrice,
      salePrice: item.salePrice,
      operationalCost: item.operationalCost,
      tax: item.tax,
      profit: item.profit,
      margin: item.margin,
      quantity: item.quantity,
      stock: item.stock ?? null,
      currency: item.currency ?? 'BRL',
      searchDate: item.searchDate ?? null,
      createdAt,
    }))

    const snapshot: QuoteSnapshot = {
      id,
      quoteId: input.quoteId,
      version,
      versionLabel: SnapshotVersion.label(version),
      status: input.lockedPrice ? 'LOCKED' : 'ACTIVE',
      createdBy,
      createdAt,
      validUntil: addDays(createdAt, input.validUntilDays),
      validityDays: input.validUntilDays,
      lockedPrice: Boolean(input.lockedPrice),
      notes: input.notes ?? '',
      customerId: input.customerId,
      customerName: input.customerName,
      quoteCode: input.quoteCode,
      quoteTitle: input.quoteTitle,
      discountPercent: input.discountPercent,
      discountValue: input.discountValue,
      freight: input.freight,
      totals: input.totals,
      items,
      priceEnginePayload: input.priceEnginePayload,
      meta: {
        engine: 'snapshot-engine',
        preparedFor: ['master-search', 'financial-panel', 'opportunities', 'crm', 'ai'],
      },
    }

    const validation = this.validator.validateSnapshot(snapshot)
    if (validation.length > 0) throw new Error(validation.join('; '))

    const all = [snapshot, ...this.listAll()]
    writeJson(SNAPSHOTS_KEY, 'snapshots', all)
    this.pushHistory(snapshot.id, version === 1 ? 'created' : 'version_created', '', snapshot.versionLabel, createdBy)
    return snapshot
  }

  duplicate(snapshotId: string, userId = 'admin@masterhub.com'): QuoteSnapshot {
    const source = this.getById(snapshotId)
    if (!source) throw new Error('Snapshot não encontrado')

    return this.create({
      quoteId: source.quoteId,
      quoteCode: source.quoteCode,
      quoteTitle: source.quoteTitle,
      customerId: source.customerId,
      customerName: source.customerName,
      validUntilDays: source.validityDays,
      lockedPrice: false,
      notes: `Cópia de ${source.versionLabel}. ${source.notes}`.trim(),
      createdBy: userId,
      discountPercent: source.discountPercent,
      discountValue: source.discountValue,
      freight: source.freight,
      totals: { ...source.totals },
      items: source.items.map((item) => ({
        productId: item.productId,
        description: item.description,
        supplier: item.supplier,
        supplierCode: item.supplierCode,
        supplierLink: item.supplierLink,
        purchasePrice: item.purchasePrice,
        pixPrice: item.pixPrice,
        cardPrice: item.cardPrice,
        installmentPrice: item.installmentPrice,
        salePrice: item.salePrice,
        operationalCost: item.operationalCost,
        tax: item.tax,
        profit: item.profit,
        margin: item.margin,
        quantity: item.quantity,
        stock: item.stock,
        currency: item.currency,
        searchDate: item.searchDate,
      })),
      priceEnginePayload: source.priceEnginePayload,
    })
  }

  setLocked(snapshotId: string, locked: boolean, userId = 'admin@masterhub.com'): QuoteSnapshot {
    const snapshot = this.getById(snapshotId)
    if (!snapshot) throw new Error('Snapshot não encontrado')
    if (locked === snapshot.lockedPrice) return snapshot

    const next: QuoteSnapshot = {
      ...snapshot,
      lockedPrice: locked,
      status: locked ? 'LOCKED' : this.isExpired(snapshot) ? 'EXPIRED' : 'ACTIVE',
    }
    this.replace(next)
    this.pushHistory(snapshotId, locked ? 'locked' : 'unlocked', String(snapshot.lockedPrice), String(locked), userId)
    return next
  }

  updateNotes(snapshotId: string, notes: string, userId = 'admin@masterhub.com'): QuoteSnapshot {
    const snapshot = this.getById(snapshotId)
    if (!snapshot) throw new Error('Snapshot não encontrado')
    this.validator.assertCanMutate(snapshot)
    const next = { ...snapshot, notes }
    this.replace(next)
    this.pushHistory(snapshotId, 'notes_updated', snapshot.notes, notes, userId)
    return next
  }

  /**
   * Creates a new version from an existing snapshot with selected item patches.
   * Locked snapshots cannot be mutated in-place — always versioned.
   */
  createVersionFromUpdates(
    baseSnapshotId: string,
    updates: Array<{ itemKey: string; patch: Partial<SnapshotItem> }>,
    userId = 'admin@masterhub.com',
    notes?: string,
  ): QuoteSnapshot {
    const base = this.getById(baseSnapshotId)
    if (!base) throw new Error('Snapshot não encontrado')

    const compare = this.compareEngine
    const patchedItems = base.items.map((item) => {
      const key = item.productId ? `p:${item.productId}` : `d:${item.description.trim().toLowerCase()}`
      const update = updates.find((u) => u.itemKey === key)
      if (!update) return item
      const next = { ...item, ...update.patch }
      const margin =
        next.salePrice > 0 ? Math.round(((next.profit / next.salePrice) * 100 + Number.EPSILON) * 100) / 100 : 0
      return { ...next, margin }
    })

    // Ensure keys from compare match
    void compare

    return this.create({
      quoteId: base.quoteId,
      quoteCode: base.quoteCode,
      quoteTitle: base.quoteTitle,
      customerId: base.customerId,
      customerName: base.customerName,
      validUntilDays: base.validityDays,
      lockedPrice: false,
      notes: notes ?? `Atualização a partir de ${base.versionLabel}`,
      createdBy: userId,
      discountPercent: base.discountPercent,
      discountValue: base.discountValue,
      freight: base.freight,
      totals: this.recalcTotals(patchedItems, base),
      items: patchedItems.map(({ snapshotId: _s, id: _i, createdAt: _c, ...rest }) => rest),
      priceEnginePayload: base.priceEnginePayload,
    })
  }

  applyRightItemsAsNewVersion(
    leftId: string,
    right: QuoteSnapshot,
    itemKeys: string[] | 'all',
    userId = 'admin@masterhub.com',
  ): QuoteSnapshot {
    const left = this.getById(leftId)
    if (!left) throw new Error('Snapshot não encontrado')

    const rightMap = new Map(
      right.items.map((item) => [
        item.productId ? `p:${item.productId}` : `d:${item.description.trim().toLowerCase()}`,
        item,
      ]),
    )

    const keys = itemKeys === 'all' ? [...rightMap.keys()] : itemKeys
    const updates = keys
      .map((key) => {
        const rightItem = rightMap.get(key)
        if (!rightItem) return null
        return {
          itemKey: key,
          patch: {
            supplier: rightItem.supplier,
            supplierCode: rightItem.supplierCode,
            supplierLink: rightItem.supplierLink,
            purchasePrice: rightItem.purchasePrice,
            pixPrice: rightItem.pixPrice,
            cardPrice: rightItem.cardPrice,
            installmentPrice: rightItem.installmentPrice,
            salePrice: rightItem.salePrice,
            operationalCost: rightItem.operationalCost,
            tax: rightItem.tax,
            profit: rightItem.profit,
            margin: rightItem.margin,
            quantity: rightItem.quantity,
            stock: rightItem.stock,
          } satisfies Partial<SnapshotItem>,
        }
      })
      .filter(Boolean) as Array<{ itemKey: string; patch: Partial<SnapshotItem> }>

    const created = this.createVersionFromUpdates(
      leftId,
      updates,
      userId,
      `Itens atualizados de ${right.versionLabel}`,
    )
    this.pushHistory(
      created.id,
      itemKeys === 'all' ? 'bulk_updated' : 'item_updated',
      left.versionLabel,
      created.versionLabel,
      userId,
    )
    return created
  }

  search(filters: SnapshotSearchFilters): QuoteSnapshot[] {
    return this.listAll().filter((snapshot) => {
      if (filters.quoteId && snapshot.quoteId !== filters.quoteId) return false
      if (filters.version != null && snapshot.version !== filters.version) return false
      if (filters.status && snapshot.status !== filters.status) return false
      if (filters.customer) {
        const q = filters.customer.toLowerCase()
        const hay = `${snapshot.customerName ?? ''} ${snapshot.customerId ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (filters.dateFrom && snapshot.createdAt < filters.dateFrom) return false
      if (filters.dateTo && snapshot.createdAt > filters.dateTo) return false
      if (filters.supplier) {
        const q = filters.supplier.toLowerCase()
        if (!snapshot.items.some((i) => (i.supplier ?? '').toLowerCase().includes(q))) return false
      }
      if (filters.product) {
        const q = filters.product.toLowerCase()
        if (
          !snapshot.items.some(
            (i) =>
              i.description.toLowerCase().includes(q) ||
              (i.productId ?? '').toLowerCase().includes(q) ||
              (i.supplierCode ?? '').toLowerCase().includes(q),
          )
        ) {
          return false
        }
      }
      return true
    })
  }

  listHistory(snapshotId?: string): SnapshotHistoryEntry[] {
    const all = readJson<SnapshotHistoryEntry[]>(HISTORY_KEY, 'history', [])
    if (!snapshotId) return all
    return all.filter((h) => h.snapshotId === snapshotId)
  }

  exportJson(snapshotId: string): SnapshotExportPayload {
    const snapshot = this.getById(snapshotId)
    if (!snapshot) throw new Error('Snapshot não encontrado')
    const payload: SnapshotExportPayload = {
      engine: 'snapshot-engine',
      format: 'json',
      version: 1,
      exportedAt: nowIso(),
      snapshot,
      history: this.listHistory(snapshotId),
    }
    this.pushHistory(snapshotId, 'exported', '', 'json', 'admin@masterhub.com')
    return payload
  }

  exportPdfMeta(snapshotId: string): { ok: true; message: string; snapshot: QuoteSnapshot } {
    const snapshot = this.getById(snapshotId)
    if (!snapshot) throw new Error('Snapshot não encontrado')
    this.pushHistory(snapshotId, 'exported', '', 'pdf', 'admin@masterhub.com')
    return {
      ok: true,
      message: 'PDF do snapshot preparado para motor futuro',
      snapshot,
    }
  }

  importPayload(payload: unknown, userId = 'admin@masterhub.com'): QuoteSnapshot {
    const result = this.validator.validateImportPayload(payload)
    if (!result.ok) throw new Error(result.errors.join('; '))

    const imported = result.data
    const created = this.create({
      quoteId: imported.quoteId,
      quoteCode: imported.quoteCode,
      quoteTitle: imported.quoteTitle,
      customerId: imported.customerId,
      customerName: imported.customerName,
      validUntilDays: imported.validityDays as SnapshotValidityDays,
      lockedPrice: false,
      notes: `Importado de ${imported.versionLabel}. ${imported.notes}`.trim(),
      createdBy: userId,
      discountPercent: imported.discountPercent,
      discountValue: imported.discountValue,
      freight: imported.freight,
      totals: imported.totals,
      items: imported.items.map((item) => ({
        productId: item.productId,
        description: item.description,
        supplier: item.supplier,
        supplierCode: item.supplierCode,
        supplierLink: item.supplierLink,
        purchasePrice: item.purchasePrice,
        pixPrice: item.pixPrice,
        cardPrice: item.cardPrice,
        installmentPrice: item.installmentPrice,
        salePrice: item.salePrice,
        operationalCost: item.operationalCost,
        tax: item.tax,
        profit: item.profit,
        margin: item.margin,
        quantity: item.quantity,
        stock: item.stock,
        currency: item.currency,
        searchDate: item.searchDate,
      })),
      priceEnginePayload: imported.priceEnginePayload,
    })
    this.pushHistory(created.id, 'imported', imported.versionLabel, created.versionLabel, userId)
    return created
  }

  remove(snapshotId: string): void {
    const snapshot = this.getById(snapshotId)
    if (!snapshot) throw new Error('Snapshot não encontrado')
    if (snapshot.lockedPrice) throw new Error('Não é possível excluir snapshot com preço travado')
    writeJson(
      SNAPSHOTS_KEY,
      'snapshots',
      this.listAll().filter((s) => s.id !== snapshotId),
    )
  }

  resetMemory(): void {
    memory.snapshots = null
    memory.history = null
  }

  private recalcTotals(items: SnapshotItem[], base: QuoteSnapshot): QuoteSnapshot['totals'] {
    const purchaseTotal = round2(items.reduce((s, i) => s + i.purchasePrice * i.quantity, 0))
    const saleTotal = round2(items.reduce((s, i) => s + i.salePrice * i.quantity, 0))
    const profitTotal = round2(items.reduce((s, i) => s + i.profit * i.quantity, 0))
    const margin = saleTotal > 0 ? round2((profitTotal / saleTotal) * 100) : 0
    return {
      purchaseTotal,
      saleTotal: saleTotal || base.totals.saleTotal,
      profitTotal,
      margin,
    }
  }

  private replace(next: QuoteSnapshot): void {
    const all = this.listAll().map((s) => (s.id === next.id ? next : s))
    writeJson(SNAPSHOTS_KEY, 'snapshots', all)
  }

  private isExpired(snapshot: QuoteSnapshot): boolean {
    if (snapshot.lockedPrice) return false
    return new Date(snapshot.validUntil).getTime() < Date.now()
  }

  private refreshOne(snapshot: QuoteSnapshot): QuoteSnapshot {
    if (snapshot.lockedPrice) {
      if (snapshot.status !== 'LOCKED') {
        const next = { ...snapshot, status: 'LOCKED' as SnapshotStatus }
        this.replace(next)
        return next
      }
      return snapshot
    }
    if (this.isExpired(snapshot) && snapshot.status !== 'EXPIRED') {
      const next = { ...snapshot, status: 'EXPIRED' as SnapshotStatus }
      this.replace(next)
      this.pushHistory(snapshot.id, 'expired', snapshot.status, 'EXPIRED', 'system')
      return next
    }
    return snapshot
  }

  private refreshStatuses(list: QuoteSnapshot[]): QuoteSnapshot[] {
    let dirty = false
    const next = list.map((snapshot) => {
      if (snapshot.lockedPrice) {
        if (snapshot.status !== 'LOCKED') {
          dirty = true
          return { ...snapshot, status: 'LOCKED' as SnapshotStatus }
        }
        return snapshot
      }
      if (this.isExpired(snapshot) && snapshot.status !== 'EXPIRED') {
        dirty = true
        return { ...snapshot, status: 'EXPIRED' as SnapshotStatus }
      }
      return snapshot
    })
    if (dirty) writeJson(SNAPSHOTS_KEY, 'snapshots', next)
    return next
  }

  private pushHistory(
    snapshotId: string,
    event: SnapshotHistoryEvent | string,
    oldValue: string,
    newValue: string,
    userId: string,
  ): void {
    const entry: SnapshotHistoryEntry = {
      id: crypto.randomUUID(),
      snapshotId,
      event,
      oldValue,
      newValue,
      userId,
      createdAt: nowIso(),
    }
    const history = [entry, ...readJson<SnapshotHistoryEntry[]>(HISTORY_KEY, 'history', [])].slice(0, 500)
    writeJson(HISTORY_KEY, 'history', history)
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const snapshotService = new SnapshotService()
